import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconComponent, TooltipDirective } from '@mode/capra';
import { ChartTypes } from '@mode/shared/contract-common';
import { LabeledMenuComponent, ToggleSwitchComponent } from '@mode/shared/ui';
import * as TextFormattingHelpers from './../text-formatting-helpers';

export type UrlType = 'basic' | 'custom';

export const BASE_FORMAT: ChartTypes.TextFormat.UrlType = {
  ...TextFormattingHelpers.getBaseFormatByShortcutType(ChartTypes.FormatShortcutType.Url),
};

@Component({
  standalone: true,
  selector: 'mode-url-format-pane',
  templateUrl: './url-format-pane.component.html',
  styleUrls: ['./url-format-pane.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, IconComponent, LabeledMenuComponent, ToggleSwitchComponent, TooltipDirective],
})
export class UrlFormatPaneComponent implements OnChanges {
  @Input({ required: true }) format!: ChartTypes.TextFormat.UrlType;

  @Output() formatChange = new EventEmitter<ChartTypes.TextFormat.UrlType>();

  readonly UrlDisplayMenuItems = TextFormattingHelpers.UrlDisplayMenuItems;
  readonly UrlLinkColorMenuItems = TextFormattingHelpers.UrlLinkColorItems;
  readonly UrlAddressUsageInfo = `Use curly brackets { } within your URL to reference a specific column's values. Only columns in the table can be referenced.`;
  readonly EncodeUrlInfoTooltip = `Encode unsupported characters for this URL  (e.g. \` % [ ])`;

  urlType: 'basic' | 'custom' = 'basic';
  selectedDisplay: TextFormattingHelpers.UrlDisplayMenuItem | undefined;
  selectedLinkColor: TextFormattingHelpers.UrlLinkColorMenuItem | undefined;
  isCustom = false;

  ngOnChanges() {
    this.initSelected();
  }

  initSelected() {
    this.format = {
      ...BASE_FORMAT,
      ...this.format,
    };

    this.urlType = this.isCustom || this.format.linkDisplayText || this.format.linkURLPattern ? 'custom' : 'basic';
    this.selectedDisplay = TextFormattingHelpers.findOrFirst(this.UrlDisplayMenuItems, this.format.linkStyle);
    this.selectedLinkColor = this.UrlLinkColorMenuItems.find((item) => item.value === this.format.linkColor);
  }

  urlTypeSelected(url: UrlType) {
    this.urlType = url;
    this.isCustom = this.urlType === 'custom';
    if (this.urlType === 'basic') {
      // Clear the display text and url pattern. Those distinguish basic from custom.
      this.apply({ linkDisplayText: undefined, linkURLPattern: undefined });
    }
  }

  onChangeDisplay(display: TextFormattingHelpers.UrlDisplayMenuItem) {
    this.apply({ linkStyle: display.value });
  }

  onChangeLinkColor(display: TextFormattingHelpers.UrlLinkColorMenuItem) {
    this.apply({ linkColor: display.value });
  }

  onChangeDisplayText(value: string) {
    this.apply({ linkDisplayText: value });
  }

  onChangeUrlAddress(value: string) {
    this.apply({ linkURLPattern: value });
  }

  onUrlAddressKeyUp(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Enter' && input != null) {
      // Enter should submit
      input.blur();
    }
  }

  toggleEncodeUrl(enabled: boolean) {
    this.apply({ shouldEncodeURL: enabled });
  }

  apply(props: Partial<ChartTypes.TextFormat.UrlType>) {
    this.format = { ...this.format, ...props };
    this.formatChange.emit(this.format);
  }
}
